import { Link } from 'react-router-dom'

/**
 * Reached from the marketing site's "Get a free estimate" CTA (01 §3.1), and by anyone who comes
 * back to the studio directly.
 *
 * **The hero shows a room, because the headline promises one.** The first version was a column
 * of type in the middle of the screen: at 1920×1080 the content occupied about 730 px of a
 * 1920 px viewport with roughly 250 px of empty ground above it and 230 px below, and a page
 * whose entire proposition is "see it in your room first" demonstrated nothing. A photograph of
 * an ASB bathroom with the studio's own selection outline drawn on it says what three sentences
 * of body copy were trying to say, and it fills the half of the screen that was empty.
 *
 * The photograph is ASB's own project work, already published on asbhomerenovations.com. It is
 * deliberately *not* a homeowner's render: those belong to the people whose bathrooms they are
 * (08 §2), and the marketing photography is the same room quality without that problem.
 *
 * The outline is drawn in markup rather than baked into the image so it stays crisp at every
 * width, matches the canvas's real selection colour from the same token, and can never drift
 * out of sync with a re-exported asset.
 */
export function Landing() {
  return (
    <div className="mx-auto w-full max-w-[1200px] px-5 py-10 lg:py-16">
      {/* Three siblings, explicitly placed, so the order differs by device without rendering
          anything twice. On a phone: headline, the two buttons, **then the picture**, then the
          steps — the picture is the first thing a thumb scrolls to, and the call to action is
          still above the fold. On a laptop: the text and the steps stack in the left column and
          the picture spans both rows beside them. */}
      <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] lg:gap-14">
        <div className="lg:col-start-1 lg:row-start-1">
          <p className="eyebrow">Free · No obligation</p>
          <h1 className="mt-4 text-4xl sm:text-5xl">See it in your room first.</h1>
          <p className="mt-5 max-w-xl text-lg">
            Tell us what you are thinking about, pick a time for Andrew to visit, and — if you want
            — take a photo of the room and swap in real products from the stores we buy from.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/estimate"
              className="rounded-btn bg-blue px-7 py-3.5 font-bold text-white shadow-blue transition-colors hover:bg-blue-deep"
            >
              Get a free estimate
            </Link>
            <Link
              to="/resume"
              className="rounded-btn border border-line-2 bg-white px-7 py-3.5 font-semibold text-ink"
            >
              I already started
            </Link>
          </div>
        </div>

        <Demo />

        <ol className="grid gap-3 sm:grid-cols-3 lg:col-start-1 lg:row-start-2">
          {[
            ['1', 'Tell us about it', 'A short form. Two minutes, no account.'],
            ['2', 'Pick a visit time', "Andrew's real availability, updated live."],
            ['3', 'Design it (optional)', 'Photo, real products, see it before he arrives.'],
          ].map(([step, title, body]) => (
            <li key={step} className="rounded-card border border-line bg-white p-4 shadow-soft">
              <span className="font-mono text-xs font-semibold text-blue-deep">{step}</span>
              <h2 className="mt-1.5 text-base">{title}</h2>
              <p className="mt-1 text-sm text-muted">{body}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

/**
 * The studio, standing still.
 *
 * Not a video and not an animation: a still frame of the real screen, which loads instantly on a
 * phone on a driveway and says the same thing. The selection outline and the product chip are
 * the two things a person has to understand before the "Get a free estimate" button means
 * anything — *you tap a thing, you get a real product with a real price* — so those are the two
 * things drawn.
 */
function Demo() {
  return (
    <figure className="relative m-0 lg:col-start-2 lg:row-span-2 lg:row-start-1">
      <div className="relative overflow-hidden rounded-img bg-navy-2 shadow-lift">
        <img
          src="/img/room-bath.jpg"
          alt="An ASB bathroom, with the vanity outlined the way the studio outlines it when you tap."
          width={720}
          height={960}
          className="block h-auto w-full"
        />

        {/* The selection, in the canvas's own colour. Percentages so it tracks the photo at
            every width rather than needing a breakpoint per size. */}
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-[6px] border-2 border-blue bg-blue/10"
          style={{ left: '14%', top: '62%', width: '68%', height: '26%' }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-pill bg-navy/85 px-3 py-1.5 text-xs font-semibold text-white"
          style={{ left: '14%', top: '55%' }}
        >
          Vanity
        </span>

        <div className="pointer-events-none absolute inset-x-3 bottom-3 flex items-center gap-3 rounded-card bg-white/95 p-2.5 shadow-soft">
          <img
            src="/img/room-vanity.jpg"
            alt=""
            width={96}
            height={96}
            className="size-12 shrink-0 rounded-btn object-cover"
          />
          <div className="min-w-0">
            <p className="m-0 truncate text-sm font-semibold text-ink">
              25 in. Single Sink Bath Vanity
            </p>
            {/* 01 §7: a material price, its store and its date — never a total, and never a
                number this page made up. This one is real, from the live catalogue. */}
            <p className="m-0 text-xs text-muted">$159.00 each · Home Depot · estimated on site</p>
          </div>
        </div>
      </div>
      <figcaption className="mt-3 text-center text-xs text-muted">
        A real ASB bathroom. Tap anything in your own photo and the studio does this.
      </figcaption>
    </figure>
  )
}
